import type Store from 'ember-data/store';
import type GenericResourceModel from 'garaje/models/generic-resource';
import type MapFeatureModel from 'garaje/models/map-feature';
import type AjaxService from 'garaje/services/ajax';
import { GENERIC_RESOURCE_TYPES } from 'garaje/utils/enums';
import normalizeResponse from 'garaje/utils/normalize-response';
import urlBuilder from 'garaje/utils/url-builder';

export interface BulkResourceUpdatePayload {
  op: 'ADD' | 'UPDATE' | 'DELETE';
  data: {
    name: string;
    resourceTypeId: number;
    geometry: {
      type: string;
      coordinates: number[];
    };
    amenities: { id: number }[];
    assignments: { employeeId: number }[];
    floorId: number;
    relationId: string;
    id?: string;
    draftId?: string;
  };
}

function getResourceOperation(resource: GenericResourceModel) {
  // eslint-disable-next-line no-extra-boolean-cast
  if (Boolean(resource.isDeleted)) {
    return 'DELETE';
  }

  // eslint-disable-next-line no-extra-boolean-cast
  if (Boolean(resource.isNew)) {
    return 'ADD';
  }

  return 'UPDATE';
}

export function transformDirtyResources(
  dirtyResources: GenericResourceModel[],
  features: MapFeatureModel[],
  locationId: string,
  draftId?: string,
): BulkResourceUpdatePayload[] {
  return dirtyResources.map((resource: GenericResourceModel) => {
    const feature = features.find((feature: MapFeatureModel) => feature.externalId === resource.id);
    if (!feature) {
      throw new Error('Feature not found');
    }
    const relationId = `urn:envoy:location:${locationId}`;
    const resourceTypeId = 2;
    return {
      op: getResourceOperation(resource),
      data: {
        id: !resource.isNew ? resource.id : undefined,
        name: resource.name,
        geometry: {
          type: feature?.geometry.type || '',
          coordinates: feature?.geometry.coordinates || [],
        },
        resourceTypeId,
        amenities: resource?.amenities || [],
        assignments: resource?.assignments || [],
        neighborhoodIds: resource?.neighborhoodIds || [],
        floorId: parseInt(feature.belongsTo('mapFloor').id()),
        relationId,
        draftId,
      },
    };
  });
}

export async function postGenericResources(ajax: AjaxService, payload: BulkResourceUpdatePayload[]): Promise<unknown> {
  const url = urlBuilder.rms.bulkResourcesUpdate();

  return await ajax.request(url, {
    contentType: 'application/json',
    type: 'POST',
    data: JSON.stringify({ data: payload }),
  });
}

export function unloadDirtyRecords(dirtyResources: GenericResourceModel[], dirtyFeatures: MapFeatureModel[]): void {
  dirtyResources.forEach((resource) => {
    resource.unloadRecord();
  });

  dirtyFeatures
    .filter((feature) => GENERIC_RESOURCE_TYPES.includes(feature.type))
    .forEach((feature) => {
      feature.unloadRecord();
    });
}

function updateResourceRecords(store: Store, response: unknown): void {
  const normalized = normalizeResponse(store, 'generic-resource', response);
  store.push(normalized);
}

export async function bulkUpdateResources(
  dirtyResources: GenericResourceModel[],
  dirtyFeatures: MapFeatureModel[],
  features: MapFeatureModel[],
  locationId: string,
  ajax: AjaxService,
  store: Store,
  draftId?: string,
): Promise<void> {
  if (!dirtyResources.length) return;

  const payload = transformDirtyResources(dirtyResources, features, locationId, draftId);
  const response = await postGenericResources(ajax, payload);
  unloadDirtyRecords(dirtyResources, dirtyFeatures);
  updateResourceRecords(store, response);
}
